/**
 * The AI agents' drawings (docs/refactor/25-agentes-no-escritorio.md): Robots, Orbs and Mechatronic Pets, in the
 * office's own style — 2D cartoon, dark outlines, the scene's soft colours, legible small. One component for all
 * three families: the look says which body, the state says the face and the sign over the head.
 *
 * Origin and size match the companion it replaces (AgentFigure): centred on the body, the shadow at y = 24, about
 * 50 units tall, so a caller places it the same way. Animations are CSS (styles/scene.css, `bh-ag*`), and stop
 * with the rest of the scene when the person asks for reduced motion.
 */

import { memo, type ReactNode } from 'react'
import { colorOf, modelOf, type AgentLook, type AgentModel, type AgentState } from './agentLooks'
import { ChosenHat } from './ProItems'
import type { HatKind } from './wardrobe'

const INK = '#1a1f2c'
const SHELL = '#E9EEF6'
const VISOR = '#0F1C2E'
const STROKE = 1.4

const EYE: Record<AgentState, string> = {
  idle: '#63D2FF',
  working: '#63D2FF',
  waiting: '#F2C45A',
  reviewing: '#B79CFF',
  completed: '#5CE1A6',
  error: '#FF6B6B',
  offline: '#6B7A90',
}

export const AgentAvatar = memo(function AgentAvatar({ look, state, title }: { look: AgentLook; state: AgentState; title?: string }) {
  const model = modelOf(look)
  const color = colorOf(look)
  return (
    <g className={`bh-ag bh-ag--${look.family} bh-ag--${state}`} data-agent-look={`${look.family}:${model.id}`} data-agent-state={state}>
      {title && <title>{title}</title>}
      <ellipse cx="0" cy="24" rx={look.family === 'orb' ? 9 : 12} ry="2.8" fill="rgba(0,0,0,0.3)" className="bh-ag__shadow" />
      <g className="bh-ag__body">
        {look.family === 'robot' && <Robot color={color} model={model} state={state} />}
        {look.family === 'orb' && <Orb color={color} model={model} state={state} />}
        {look.family === 'pet' && <Pet color={color} model={model} state={state} />}
        {look.hat && look.hat !== 'none' && <AgentHat family={look.family} hat={look.hat} />}
      </g>
      <StateSign state={state} />
    </g>
  )
})

/**
 * The person's hat on their agent (docs/refactor/47): the people's drawing, scaled from a head of radius 23 at
 * (0, -100) to each body's head.
 */
const HEADS: Record<AgentLook['family'], { y: number; r: number }> = {
  robot: { y: -9, r: 12 },
  orb: { y: 0, r: 13 },
  pet: { y: -6, r: 12 },
}

function AgentHat({ family, hat }: { family: AgentLook['family']; hat: HatKind }) {
  const head = HEADS[family]
  const scale = head.r / 23
  return (
    <g transform={`translate(0 ${head.y}) scale(${scale.toFixed(3)}) translate(0 100)`}>
      <ChosenHat kind={hat} />
    </g>
  )
}

/** The face every agent shares: a dark visor with two lights — closed while it rests, red when it fails. */
function Visor({ x, y, w, h, state }: { x: number; y: number; w: number; h: number; state: AgentState }) {
  const eye = EYE[state]
  const cx = x + w / 2
  const cy = y + h / 2
  const dx = w * 0.22
  const closed = state === 'idle' || state === 'offline'
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={VISOR} stroke={INK} strokeWidth={1} />
      {closed ? (
        <>
          <path d={`M ${cx - dx - 2} ${cy} q 2 1.6 4 0`} stroke={eye} strokeWidth={1.3} fill="none" strokeLinecap="round" />
          <path d={`M ${cx + dx - 2} ${cy} q 2 1.6 4 0`} stroke={eye} strokeWidth={1.3} fill="none" strokeLinecap="round" />
        </>
      ) : state === 'error' ? (
        <>
          <path d={`M ${cx - dx - 1.6} ${cy - 1.6} l 3.2 3.2 m 0 -3.2 l -3.2 3.2`} stroke={eye} strokeWidth={1.3} strokeLinecap="round" />
          <path d={`M ${cx + dx - 1.6} ${cy - 1.6} l 3.2 3.2 m 0 -3.2 l -3.2 3.2`} stroke={eye} strokeWidth={1.3} strokeLinecap="round" />
        </>
      ) : (
        <>
          <ellipse cx={cx - dx} cy={cy} rx={1.9} ry={2.3} fill={eye} className="bh-ag__eye" />
          <ellipse cx={cx + dx} cy={cy} rx={1.9} ry={2.3} fill={eye} className="bh-ag__eye" />
        </>
      )}
    </g>
  )
}

/** What the model holds while it works: the hint of a trade, nothing more. */
function Accessory({ kind, color }: { kind: AgentModel['accessory']; color: string }) {
  switch (kind) {
    case 'laptop':
      return (
        <g>
          <rect x={-6} y={-5} width={12} height={8} rx={1.2} fill="#2B3550" stroke={INK} strokeWidth={1} />
          <path d="M -2.5 -2 l -1.5 1.4 l 1.5 1.4 M 2.5 -2 l 1.5 1.4 l -1.5 1.4" stroke="#7FB3FF" strokeWidth={0.8} fill="none" />
          <rect x={-7.5} y={3} width={15} height={2} rx={1} fill="#8D99B3" stroke={INK} strokeWidth={0.8} />
        </g>
      )
    case 'paper':
      return (
        <g>
          <rect x={-5} y={-7} width={10} height={13} rx={1} fill="#F7F3E6" stroke={INK} strokeWidth={1} />
          <path d="M -3 -4 h 6 M -3 -1.5 h 6 M -3 1 h 4" stroke="#8D99B3" strokeWidth={0.8} />
        </g>
      )
    case 'magnifier':
      return (
        <g>
          <circle cx={-1} cy={-2} r={4.2} fill="rgba(160,210,255,0.35)" stroke={INK} strokeWidth={1.2} />
          <path d="M 2 1 l 4 4" stroke={INK} strokeWidth={2} strokeLinecap="round" />
        </g>
      )
    case 'wrench':
      return <path d="M -5 5 L 2 -2 a 3.5 3.5 0 1 1 3 -3 l -1.6 1.6 l -1.5 -0.3 l 0.3 1.5 Z" fill="#C7CFDD" stroke={INK} strokeWidth={1} />
    case 'shield':
      return (
        <g>
          <path d="M 0 -7 L 6 -4.5 V 0 C 6 3.5 3 6 0 7 C -3 6 -6 3.5 -6 0 V -4.5 Z" fill={color} stroke={INK} strokeWidth={1} />
          <path d="M 0 -3.2 v 6 M -3 0 h 6" stroke="#FFFFFF" strokeWidth={1.3} />
        </g>
      )
    case 'chart':
      return (
        <g>
          <rect x={-6} y={-6} width={12} height={11} rx={1.2} fill="#2B3550" stroke={INK} strokeWidth={1} />
          <path d="M -3.5 3 v -3 M 0 3 v -6 M 3.5 3 v -4" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
        </g>
      )
    case 'palette':
      return (
        <g>
          <path d="M 0 -6 C 6 -6 7 0 4 1.5 C 2 2.5 3 5 0 5 C -5 5 -7 1 -6 -2 C -5 -5 -3 -6 0 -6 Z" fill="#F2D9B8" stroke={INK} strokeWidth={1} />
          <circle cx={-3} cy={-2} r={1.2} fill="#E5625C" />
          <circle cx={0} cy={-3.5} r={1.2} fill="#5B9BF0" />
          <circle cx={3} cy={-2} r={1.2} fill="#5CC98A" />
        </g>
      )
    case 'gear':
      return (
        <g className="bh-ag__gear">
          <circle cx={0} cy={0} r={5} fill="#C7CFDD" stroke={INK} strokeWidth={1} strokeDasharray="2.2 1.4" />
          <circle cx={0} cy={0} r={2} fill={color} stroke={INK} strokeWidth={0.8} />
        </g>
      )
  }
}

/* ------------------------------------------------------------------ robots */

function Robot({ color, model, state }: { color: string; model: AgentModel; state: AgentState }) {
  return (
    <g>
      {/* Legs, then the body, then the arms: a small, compact machine. */}
      <rect x={-7} y={13} width={5} height={9} rx={2} fill={SHELL} stroke={INK} strokeWidth={STROKE} />
      <rect x={2} y={13} width={5} height={9} rx={2} fill={SHELL} stroke={INK} strokeWidth={STROKE} />
      <rect x={-10} y={1} width={20} height={15} rx={6} fill={SHELL} stroke={INK} strokeWidth={STROKE} />
      <rect x={-5} y={5} width={10} height={6} rx={2} fill={color} stroke={INK} strokeWidth={1} />
      <rect x={-14} y={3} width={5} height={10} rx={2.5} fill={SHELL} stroke={INK} strokeWidth={STROKE} className="bh-ag__arm" />
      <rect x={9} y={3} width={5} height={10} rx={2.5} fill={SHELL} stroke={INK} strokeWidth={STROKE} className="bh-ag__arm bh-ag__arm--right" />
      {/* Head: round, a visor, headphones in the model's colour, and an antenna. */}
      <line x1={0} y1={-20} x2={0} y2={-25} stroke={INK} strokeWidth={1.4} />
      <circle cx={0} cy={-26.5} r={2.2} fill={color} stroke={INK} strokeWidth={1} className="bh-ag__signal" />
      <circle cx={0} cy={-9} r={12} fill={SHELL} stroke={INK} strokeWidth={STROKE} />
      <path d="M -11 -14 A 12 12 0 0 1 11 -14" fill="none" stroke={color} strokeWidth={2.4} />
      <rect x={-14.5} y={-13} width={5} height={8} rx={2.5} fill={color} stroke={INK} strokeWidth={1} />
      <rect x={9.5} y={-13} width={5} height={8} rx={2.5} fill={color} stroke={INK} strokeWidth={1} />
      <Visor x={-8.5} y={-13} w={17} h={9} state={state} />
      {(state === 'working' || state === 'reviewing') && (
        <g transform="translate(15 10)"><g className="bh-ag__tool">
          <Accessory kind={state === 'reviewing' ? 'magnifier' : model.accessory} color={color} />
        </g></g>
      )}
    </g>
  )
}

/* ------------------------------------------------------------------ orbs */

function Orb({ color, model, state }: { color: string; model: AgentModel; state: AgentState }) {
  return (
    <g>
      {/* The far half of the ring, the sphere, then the near half: it reads as going around. */}
      <path d="M -19 2 A 19 5.5 0 0 1 19 2" fill="none" stroke={color} strokeWidth={1.6} opacity={0.6} className="bh-ag__ring" />
      <circle cx={0} cy={0} r={13} fill={color} stroke={INK} strokeWidth={STROKE} className="bh-ag__core" />
      <circle cx={-4} cy={-5} r={4} fill="rgba(255,255,255,0.45)" />
      <Visor x={-8} y={-4.5} w={16} h={9} state={state} />
      <path d="M -19 2 A 19 5.5 0 0 0 19 2" fill="none" stroke={color} strokeWidth={1.8} className="bh-ag__ring" />
      <g className="bh-ag__particles">
        <circle cx={-17} cy={-8} r={1.4} fill={color} />
        <circle cx={16} cy={-11} r={1.1} fill={color} />
        <circle cx={12} cy={13} r={1.2} fill={color} />
      </g>
      {/* A small base light: it floats, so no chair and no legs. */}
      <ellipse cx={0} cy={18} rx={6} ry={1.8} fill={color} opacity={0.35} className="bh-ag__thrust" />
      {(state === 'working' || state === 'reviewing') && (
        <g transform="translate(19 -2) scale(0.85)"><g className="bh-ag__tool">
          <Accessory kind={state === 'reviewing' ? 'magnifier' : model.accessory} color={color} />
        </g></g>
      )}
    </g>
  )
}

/* ------------------------------------------------------------------ mechatronic pets */

function Pet({ color, model, state }: { color: string; model: AgentModel; state: AgentState }) {
  const species = model.species ?? 'cat'
  const fur = species === 'panda' ? SHELL : color
  const dark = '#2A2F3D'
  const ears: Record<string, ReactNode> = {
    cat: (
      <>
        <path d="M -11 -14 L -8 -24 L -2 -17 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M 11 -14 L 8 -24 L 2 -17 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M -8.5 -16 L -7.8 -20.5 L -5 -17.5 Z M 8.5 -16 L 7.8 -20.5 L 5 -17.5 Z" fill="#8D99B3" />
      </>
    ),
    fox: (
      <>
        <path d="M -12 -13 L -10 -27 L -2 -17 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M 12 -13 L 10 -27 L 2 -17 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
      </>
    ),
    dog: (
      <>
        <ellipse cx={-12} cy={-9} rx={4} ry={8} fill={dark} stroke={INK} strokeWidth={STROKE} transform="rotate(18 -12 -9)" />
        <ellipse cx={12} cy={-9} rx={4} ry={8} fill={dark} stroke={INK} strokeWidth={STROKE} transform="rotate(-18 12 -9)" />
      </>
    ),
    bunny: (
      <>
        <rect x={-8.5} y={-33} width={6} height={18} rx={3} fill={fur} stroke={INK} strokeWidth={STROKE} transform="rotate(-8 -5.5 -24)" />
        <rect x={2.5} y={-33} width={6} height={18} rx={3} fill={fur} stroke={INK} strokeWidth={STROKE} transform="rotate(8 5.5 -24)" />
      </>
    ),
    panda: (
      <>
        <circle cx={-10} cy={-18} r={4.5} fill={dark} stroke={INK} strokeWidth={STROKE} />
        <circle cx={10} cy={-18} r={4.5} fill={dark} stroke={INK} strokeWidth={STROKE} />
      </>
    ),
    owl: (
      <>
        <path d="M -11 -15 L -11 -23 L -5 -18 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M 11 -15 L 11 -23 L 5 -18 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
      </>
    ),
    dragon: (
      <>
        <path d="M -8 -18 L -11 -27 L -4 -20 Z" fill="#F2D9B8" stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M 8 -18 L 11 -27 L 4 -20 Z" fill="#F2D9B8" stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
      </>
    ),
    salamander: (
      <>
        <circle cx={-7.5} cy={-16} r={4.4} fill={fur} stroke={INK} strokeWidth={STROKE} />
        <circle cx={7.5} cy={-16} r={4.4} fill={fur} stroke={INK} strokeWidth={STROKE} />
        <circle cx={-7.5} cy={-16} r={1.6} fill={INK} />
        <circle cx={7.5} cy={-16} r={1.6} fill={INK} />
      </>
    ),
    turtle: null,
  }

  return (
    <g>
      {/* Behind the body: a tail, wings or a shell. */}
      {species === 'fox' && <path d="M 9 14 C 22 12 22 0 16 -2 C 18 6 14 10 8 10 Z" fill={fur} stroke={INK} strokeWidth={STROKE} />}
      {species === 'cat' && <path d="M 9 16 C 18 16 19 6 15 2" fill="none" stroke={INK} strokeWidth={3.6} strokeLinecap="round" />}
      {species === 'cat' && <path d="M 9 16 C 18 16 19 6 15 2" fill="none" stroke={fur} strokeWidth={2} strokeLinecap="round" />}
      {species === 'dragon' && (
        <>
          <path d="M -9 2 L -21 -8 L -18 4 L -22 8 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
          <path d="M 9 2 L 21 -8 L 18 4 L 22 8 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        </>
      )}
      {species === 'salamander' && (
        <>
          <path d="M 8 16 C 20 17 24 9 20 3 C 23 10 18 14 9 11 Z" fill={fur} stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
          <circle cx={16} cy={12} r={1.5} fill={INK} opacity={0.35} />
        </>
      )}
      {species === 'turtle' && <path d="M -15 16 C -15 0 15 0 15 16 Z" fill={color} stroke={INK} strokeWidth={STROKE} />}
      {species === 'turtle' && <path d="M -8 6 l 4 -3 l 4 3 l 4 -3 l 4 3 M -10 12 h 20" stroke={INK} strokeWidth={0.9} fill="none" opacity={0.55} />}

      {/* Sitting body with a metal belly plate: half animal, half machine. */}
      <ellipse cx={0} cy={13} rx={species === 'turtle' ? 13 : 10.5} ry={9.5} fill={species === 'turtle' ? '#C7CFDD' : fur} stroke={INK} strokeWidth={STROKE} />
      {species !== 'turtle' && <ellipse cx={0} cy={14.5} rx={6} ry={6} fill={species === 'panda' ? dark : '#F4F6FA'} stroke={INK} strokeWidth={1} />}
      {species !== 'turtle' && <circle cx={0} cy={14.5} r={1.4} fill="#8D99B3" />}
      <rect x={-8} y={20} width={5} height={4} rx={2} fill={species === 'panda' ? dark : fur} stroke={INK} strokeWidth={1.2} />
      <rect x={3} y={20} width={5} height={4} rx={2} fill={species === 'panda' ? dark : fur} stroke={INK} strokeWidth={1.2} />
      {species === 'salamander' && (
        <>
          <circle cx={-7} cy={9} r={2} fill={INK} opacity={0.32} />
          <circle cx={7} cy={10} r={1.7} fill={INK} opacity={0.32} />
        </>
      )}
      {species === 'owl' && (
        <>
          <path d="M -10 6 C -15 10 -14 18 -9 19 Z" fill={fur} stroke={INK} strokeWidth={1.2} />
          <path d="M 10 6 C 15 10 14 18 9 19 Z" fill={fur} stroke={INK} strokeWidth={1.2} />
        </>
      )}

      {/* Head: ears for the animal, a visor for the machine, a bolt at the temple. */}
      {ears[species]}
      {species === 'salamander'
        ? <ellipse cx={0} cy={-5} rx={12.5} ry={10} fill={fur} stroke={INK} strokeWidth={STROKE} />
        : <circle cx={0} cy={-6} r={species === 'turtle' ? 9.5 : 12} fill={species === 'turtle' ? '#9BD08A' : fur} stroke={INK} strokeWidth={STROKE} />}
      {species === 'fox' && <path d="M -7 -1 C -4 4 4 4 7 -1 C 4 1 -4 1 -7 -1 Z" fill="#F4F6FA" stroke={INK} strokeWidth={0.8} />}
      <circle cx={species === 'turtle' ? 8 : 10.5} cy={-6} r={1.8} fill="#C7CFDD" stroke={INK} strokeWidth={0.8} />
      <Visor x={species === 'turtle' ? -6.5 : -8.5} y={species === 'turtle' ? -10 : -11} w={species === 'turtle' ? 13 : 17} h={species === 'turtle' ? 7.5 : 9} state={state} />
      {(state === 'working' || state === 'reviewing') && (
        <g transform="translate(15 12) scale(0.9)"><g className="bh-ag__tool">
          <Accessory kind={state === 'reviewing' ? 'magnifier' : model.accessory} color={color} />
        </g></g>
      )}
    </g>
  )
}

/* ------------------------------------------------------------------ the sign over the head */

/** What the state says above the agent, the same for every family: zZz, an hourglass, a "?", a ✓ or an ✗. */
function StateSign({ state }: { state: AgentState }) {
  const at = 'translate(14 -30)'
  switch (state) {
    case 'idle':
      return (
        <g transform={at}><g className="bh-ag__sign bh-ag__sign--idle">
          <text x={0} y={0} fontSize={7} fontWeight={700} fill="#B8C4DA">z</text>
          <text x={4} y={-5} fontSize={5.5} fontWeight={700} fill="#B8C4DA">z</text>
        </g></g>
      )
    case 'waiting':
      return (
        <g transform={at}><g className="bh-ag__sign bh-ag__sign--waiting">
          <path d="M -3 -5 h 6 l -3 4.5 l 3 4.5 h -6 l 3 -4.5 Z" fill="#F2C45A" stroke={INK} strokeWidth={0.9} strokeLinejoin="round" />
        </g></g>
      )
    case 'reviewing':
      return (
        <g transform={at}><g className="bh-ag__sign">
          <text x={-2.5} y={2} fontSize={9} fontWeight={800} fill="#B79CFF" stroke={INK} strokeWidth={0.5}>?</text>
        </g></g>
      )
    case 'completed':
      return (
        <g transform={at}><g className="bh-ag__sign bh-ag__sign--done">
          <circle r={5} fill="#3FB979" stroke={INK} strokeWidth={0.9} />
          <path d="M -2.4 0 l 1.6 1.7 l 3.2 -3.4" stroke="#FFFFFF" strokeWidth={1.4} fill="none" strokeLinecap="round" />
        </g></g>
      )
    case 'error':
      return (
        <g transform={at}><g className="bh-ag__sign">
          <circle r={5} fill="#E5484D" stroke={INK} strokeWidth={0.9} />
          <path d="M -2 -2 l 4 4 m 0 -4 l -4 4" stroke="#FFFFFF" strokeWidth={1.4} strokeLinecap="round" />
        </g></g>
      )
    default:
      return null
  }
}
